import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './config';

// Users/Officers collection
const getUsersCollection = () => {
  return collection(db, 'users');
};

// Create or update a user/officer
export const saveUser = async (userData) => {
  try {
    const usersRef = getUsersCollection();

    if (userData.id) {
      // Update existing user
      const userRef = doc(db, 'users', userData.id);
      await updateDoc(userRef, {
        name: userData.name,
        email: userData.email.toLowerCase(), // Store email in lowercase
        initials: userData.initials,
        role: userData.role || 'Varsity',
        permissions: userData.permissions || {
          sessionHost: false,
          pointTracking: false
        },
        updatedAt: serverTimestamp()
      });
      return { success: true, id: userData.id };
    } else {
      // Create new user
      const docRef = await addDoc(usersRef, {
        name: userData.name,
        email: userData.email.toLowerCase(), // Store email in lowercase
        initials: userData.initials,
        role: userData.role || 'Varsity',
        permissions: userData.permissions || {
          sessionHost: false,
          pointTracking: false
        },
        defaultPassword: 'Debate212', // Store for reference
        needsPasswordChange: true, // Flag to prompt password change on first login
        createdAt: serverTimestamp()
      });
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    console.error('Error saving user:', error);
    return { success: false, error };
  }
};

// Get all users
export const getUsers = async () => {
  try {
    const usersRef = getUsersCollection();
    const q = query(usersRef, orderBy('name'));
    const querySnapshot = await getDocs(q);
    const users = [];
    querySnapshot.forEach((doc) => {
      users.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, users };
  } catch (error) {
    console.error('Error getting users:', error);
    return { success: false, error };
  }
};

// Get users with specific permission
export const getUsersByPermission = async (permissionType) => {
  try {
    const usersRef = getUsersCollection();
    const querySnapshot = await getDocs(usersRef);
    const users = [];

    querySnapshot.forEach((doc) => {
      const userData = doc.data();
      if (userData.permissions && userData.permissions[permissionType]) {
        users.push({ id: doc.id, ...userData });
      }
    });

    return { success: true, users };
  } catch (error) {
    console.error('Error getting users by permission:', error);
    return { success: false, error };
  }
};

// Get session hosts (users with sessionHost permission)
export const getSessionHosts = async () => {
  return getUsersByPermission('sessionHost');
};

// Get point trackers (users with pointTracking permission)
export const getPointTrackers = async () => {
  return getUsersByPermission('pointTracking');
};

// Delete a user
export const deleteUser = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await deleteDoc(userRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting user:', error);
    return { success: false, error };
  }
};

// Update user permissions
export const updateUserPermissions = async (userId, permissions) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      permissions,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating user permissions:', error);
    return { success: false, error };
  }
};

// Get user by email (case-insensitive)
export const getUserByEmail = async (email) => {
  try {
    const usersRef = getUsersCollection();
    // Convert email to lowercase for case-insensitive matching
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return { success: true, user: null };
    }

    const userDoc = querySnapshot.docs[0];
    return { success: true, user: { id: userDoc.id, ...userDoc.data() } };
  } catch (error) {
    console.error('Error getting user by email:', error);
    return { success: false, error };
  }
};

// Get user by Firebase Auth UID
export const getUserByAuthId = async (authUid) => {
  try {
    const userRef = doc(db, 'users', authUid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return { success: true, user: null };
    }

    return { success: true, user: { id: userSnap.id, ...userSnap.data() } };
  } catch (error) {
    console.error('Error getting user by auth ID:', error);
    return { success: false, error };
  }
};

// Reset user password to default (Debate212)
// Sets needsPasswordChange flag and calls Cloud Function to delete Firebase Auth account
export const resetUserPassword = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return { success: false, error: 'User not found' };
    }

    const userData = userSnap.data();

    // Update user to require password change
    await updateDoc(userRef, {
      needsPasswordChange: true,
      updatedAt: serverTimestamp()
    });

    // Call Cloud Function to delete Firebase Auth account (if it exists)
    try {
      const resetAuthPassword = httpsCallable(functions, 'resetUserAuthPassword');
      const result = await resetAuthPassword({ email: userData.email });

      return {
        success: true,
        user: { id: userId, ...userData },
        email: userData.email,
        authDeleted: !result.data.userNotFound,
        message: result.data.message
      };
    } catch (cloudFunctionError) {
      // If cloud function fails, still return success since Firestore was updated
      console.warn('Cloud function error (non-critical):', cloudFunctionError);
      return {
        success: true,
        user: { id: userId, ...userData },
        email: userData.email,
        authDeleted: false,
        warning: 'Password reset in database, but could not delete auth account. User may need to contact admin.'
      };
    }
  } catch (error) {
    console.error('Error resetting user password:', error);
    return { success: false, error };
  }
};

// Clear the needsPasswordChange flag after user changes password
export const clearPasswordChangeFlag = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      needsPasswordChange: false,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error clearing password change flag:', error);
    return { success: false, error };
  }
};

// Create or ensure user profile exists for authenticated user
// Uses the Firebase Auth UID as the document ID
// If a profile exists with the same email but different ID, it will be migrated
export const ensureUserProfile = async (authUser, makeAdmin = false) => {
  try {
    const userRef = doc(db, 'users', authUser.uid);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      // User already exists with correct auth ID
      return { success: true, user: { id: userSnap.id, ...userSnap.data() }, created: false };
    }

    // Check if a user profile exists with this email but different document ID
    const existingUserResult = await getUserByEmail(authUser.email);

    if (existingUserResult.success && existingUserResult.user) {
      // Found existing profile by email - migrate it to use the auth UID
      const existingUser = existingUserResult.user;

      // Create new document with auth UID as ID, copying existing data
      await setDoc(userRef, {
        name: existingUser.name,
        email: existingUser.email.toLowerCase(), // Store email in lowercase
        initials: existingUser.initials,
        permissions: existingUser.permissions || {
          sessionHost: makeAdmin,
          pointTracking: makeAdmin,
          admin: makeAdmin
        },
        createdAt: existingUser.createdAt || serverTimestamp(),
        migratedFrom: existingUser.id, // Track where this came from
        migratedAt: serverTimestamp()
      });

      // Optionally delete the old document (commented out for safety)
      // await deleteDoc(doc(db, 'users', existingUser.id));

      return {
        success: true,
        user: { id: authUser.uid, ...existingUser },
        created: false,
        migrated: true
      };
    }

    // No existing profile found - create new one
    const userData = {
      name: authUser.displayName || authUser.email.split('@')[0],
      email: authUser.email.toLowerCase(), // Store email in lowercase
      initials: authUser.displayName
        ? authUser.displayName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
        : authUser.email.substring(0, 2).toUpperCase(),
      permissions: {
        sessionHost: makeAdmin, // Set to true if making admin
        pointTracking: makeAdmin,
        admin: makeAdmin
      },
      createdAt: serverTimestamp()
    };

    await setDoc(userRef, userData);
    return { success: true, user: { id: authUser.uid, ...userData }, created: true };
  } catch (error) {
    console.error('Error ensuring user profile:', error);
    return { success: false, error };
  }
};
